import { FormControlLabel, Grid, Skeleton, Switch, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useQueries } from '@tanstack/react-query';
import { compact, flatMap, forEach, isEmpty, keyBy, map, uniq, values } from 'lodash';
import React, { useEffect, useMemo, useRef } from 'react';

import { getModel } from 'api/platform';
import { modelTypeCellSegmentation } from 'components/Pages/Jobs/inferenceFieldsOptions';
import CellRulesFromPanelSelect from 'components/StudyDashboard/ProceduresPage/Actions/Multiplex/CellRulesFromPanelSelect';
import { CellTypingJobSelect } from 'components/StudyDashboard/ProceduresPage/Actions/Multiplex/CellTypingJobSelect';
import { NeighborhoodClustersJobSelect } from 'components/StudyDashboard/ProceduresPage/Actions/Multiplex/NeighborhoodClustersJobSelect';
import { ThresholdJobMultiSelect } from 'components/StudyDashboard/ProceduresPage/Actions/Multiplex/ThresholdJobMultiSelect';
import { ThresholdJobSelect } from 'components/StudyDashboard/ProceduresPage/Actions/Multiplex/ThresholdJobSelect';
import { AnnotationAssignment } from 'interfaces/annotation';
import {
  ClassificationBinningParams,
  getResultsByStainByResultName,
  ModelInference,
  ResultNameOrchestrations,
  SlideInferenceResults,
} from 'interfaces/calculateFeatures';
import { CellTypingJob, NeighborhoodClustersJob, ThresholdJob } from 'interfaces/job';
import { CellRules } from 'interfaces/jobs/multiplex/cellTypingParams';
import { PipRunNameEnum } from 'interfaces/jobs/pipsRunNames';
import { useAssignmentManagement } from 'utils/assignmentManagement';
import { getModelId, getModelsByStain, ModelPreferencesById, ModelUserPreferences } from 'utils/models';
import { useOrchestrationSelections } from 'utils/orchestrationSelections';
import { useJobsByOrchestrationIds } from 'utils/queryHooks/jobs/useJobs';
import { useSlideRegistrations } from 'utils/slideRegistrations';
import { BinaryClassifierSelectionMode } from '../binaryClassifierSelections';
import { inputCategoryLabels, MissingInputsBySlideId } from '../calculateFeaturesInputs';
import {
  buildLatestCellSegmentationSelectionUpdates,
  buildLatestSpotDetectionSelectionUpdates,
  hasSelectedPostProcessingInputs,
} from '../defaultLatestInputSelections';
import PreviousJobComparisonAlert from '../PreviousJobComparisonAlert';
import type { PreviousJobComparisonStatus } from '../previousJobComparisonStatus';
import {
  getConfirmedCellSegmentationGeometries,
  getSelectedCellSegmentationGeometries,
} from '../spotDetectionInputSetup';
import type { ThresholdConflictResolutions, ThresholdMergeState } from '../thresholdMerge';
import {
  collectOrchestrationIdsFromModelsByStain,
  collectOrchestrationIdsFromOrchestrationsByStain,
  JobsByOrchestrationIdProvider,
} from './JobsByOrchestrationIdContext';
import AssignmentAnnotationsSection from './sections/AssignmentAnnotationsSection';
import GenericResultsSection from './sections/GenericResultsSection';
import InferenceModelsSection from './sections/InferenceModelsSection';
import Summary from './Summary';
import ThresholdMergePreview from './ThresholdMergePreview';
import { shouldRevealOptionalSelector, useGeneralInputAvailability } from './useGeneralInputAvailability';

export type ThresholdJobSelectionMode = 'single' | 'multiple';

export interface MultiplexGeneralInputsProps {
  thresholdJobSelectionMode: ThresholdJobSelectionMode;
  setThresholdJobSelectionMode: (mode: ThresholdJobSelectionMode) => void;
  selectedThresholdJobId: string | null;
  setSelectedThresholdJobId: (jobId: string | null) => void;
  selectedThresholdJobIds: string[];
  setSelectedThresholdJobIds: (jobIds: string[]) => void;
  thresholdMergeState: ThresholdMergeState;
  setThresholdConflictResolutions: React.Dispatch<React.SetStateAction<ThresholdConflictResolutions>>;
  restoredThresholdJobOrchestrationId?: string | null;
  onThresholdJobSelect: (job: ThresholdJob) => void;
  onThresholdJobsSelect: (jobs: ThresholdJob[]) => void;
  onCellRulesChange: (cellRules: CellRules | null) => void;
  restoredCellRules?: CellRules | null;
  selectedCellRulesPanelId: string | null | undefined;
  setSelectedCellRulesPanelId: (panelId: string | null) => void;
  selectedCellTypingJobId: string | null;
  setSelectedCellTypingJobId: (jobId: string | null) => void;
  restoredCellTypingJobOrchestrationId?: string | null;
  onCellTypingJobSelect: (job: CellTypingJob) => void;
  selectedNeighborhoodClustersJobId: string | null;
  setSelectedNeighborhoodClustersJobId: (jobId: string | null) => void;
  restoredNeighborhoodClustersJobOrchestrationId?: string | null;
  onNeighborhoodClustersJobSelect: (job: NeighborhoodClustersJob) => void;
  binaryClassifierSelectionMode: BinaryClassifierSelectionMode;
  setBinaryClassifierSelectionMode: (mode: BinaryClassifierSelectionMode) => void;
  cellTypingJobError?: string;
  pipRunName: string;
  autoAssignLastJob?: boolean;
  autoAssignLastPanel?: boolean;
}

export interface InferenceModelsForSlidesProps
  extends Partial<ReturnType<typeof useOrchestrationSelections>>,
    Partial<ReturnType<typeof useAssignmentManagement>>,
    Partial<ReturnType<typeof useSlideRegistrations>> {
  studyId: string;
  isLoading: boolean;
  slides: SlideInferenceResults[];
  inferenceModels?: ModelInference[];
  postprocessedArtifacts?: ResultNameOrchestrations[];
  pipsResultsArtifacts?: ResultNameOrchestrations[];
  pipsAreaInstancesArtifacts?: ResultNameOrchestrations[];
  cellQuantificationArtifacts?: ResultNameOrchestrations[];
  areaQuantificationArtifacts?: ResultNameOrchestrations[];
  slideSegmentationsTableArtifacts?: ResultNameOrchestrations[];
  slideDetectionsTableArtifacts?: ResultNameOrchestrations[];
  displayInferenceModels?: boolean;
  displayAssignmentAnnotations?: boolean;
  displayPostProcessing?: boolean;
  displayQuantification?: boolean;
  displayPipsResults?: boolean;
  displayPipsAreaInstancesResults?: boolean;
  displaySlideSegmentationsTable?: boolean;
  displaySlideDetectionsTable?: boolean;
  hidePostProcessingInputs?: boolean;
  setHidePostProcessingInputs?: (hidePostProcessingInputs: boolean) => void;
  isLoadingInferenceResults?: boolean;
  isLoadingAssignmentAnnotations?: boolean;
  isLoadingPostProcessing?: boolean;
  isLoadingPipsResults?: boolean;
  isLoadingPipsAreaInstances?: boolean;
  isLoadingQuantification?: boolean;
  isLoadingSlideSegmentationsTable?: boolean;
  isLoadingSlideDetectionsTable?: boolean;
  assignmentAnnotationsByStainType?: { [stainType: string]: AnnotationAssignment[] };
  modelsType: string[];
  setModelPreferencesByModelId?: (modelId: string, userPreferences: ModelUserPreferences) => void;
  modelPreferencesByModelId?: ModelPreferencesById;
  setIntensityClassificationBinning?: (modelId: string, binning: ClassificationBinningParams) => void;
  autoSelectLatestFallbackInputs?: boolean;
  multiplexGeneralInputsProps?: MultiplexGeneralInputsProps;
  previousJobComparisonStatus?: PreviousJobComparisonStatus;
  disableIfNoJob?: boolean;
  missingInputsBySlideId?: MissingInputsBySlideId;
  showValidationMessages?: boolean;
}

const InferenceModelsForSlides: React.FC<React.PropsWithChildren<InferenceModelsForSlidesProps>> = ({
  studyId,
  isLoading,
  slides,
  inferenceModels,
  postprocessedArtifacts,
  pipsResultsArtifacts,
  pipsAreaInstancesArtifacts,
  cellQuantificationArtifacts,
  areaQuantificationArtifacts,
  slideSegmentationsTableArtifacts,
  slideDetectionsTableArtifacts,
  modelsType,
  isLoadingInferenceResults = false,
  isLoadingAssignmentAnnotations = false,
  isLoadingPostProcessing = false,
  isLoadingPipsResults = false,
  isLoadingPipsAreaInstances = false,
  isLoadingQuantification = false,
  isLoadingSlideSegmentationsTable = false,
  isLoadingSlideDetectionsTable = false,
  displayInferenceModels = true,
  displayAssignmentAnnotations = false,
  displayPostProcessing = false,
  displayQuantification = false,
  displayPipsResults = false,
  displayPipsAreaInstancesResults = false,
  hidePostProcessingInputs = false,
  setHidePostProcessingInputs,
  displaySlideSegmentationsTable = false,
  displaySlideDetectionsTable = false,
  assignmentAnnotationsByStainType,
  selectedAssignments,
  updateSelectedAssignment,
  addNewOverrideAssignment,
  removeSelectedAssignment,
  selectedOrchestrations,
  setSelectedOrchestrationsByClassificationType,
  setSelectedOrchestrationsByTypeByStain,
  selectedPostprocessedOrchestrations,
  updateSelectedPostprocessedOrchestrations,
  setSelectedPostprocessedOrchestrationsByFlowClassNameByStain,
  selectedPipsAreaInstancesOrchestrations,
  updateSelectedPipsAreaInstancesOrchestrations,
  selectedSlideDetectionsTableOrchestrations,
  updateSelectedSlideDetectionsTableOrchestrations,
  selectedPipsOrchestrations,
  updateSelectedPipsOrchestrations,
  selectedCellQuantificationOrchestrations,
  updateSelectedCellQuantificationOrchestrations,
  selectedAreaQuantificationOrchestrations,
  updateSelectedAreaQuantificationOrchestrations,
  selectedSlideSegmentationsTableOrchestrations,
  updateSelectedSlideSegmentationsTableOrchestrations,
  selectedSlideRegistrations,
  modelPreferencesByModelId,
  setModelPreferencesByModelId,
  setIntensityClassificationBinning,
  autoSelectLatestFallbackInputs = false,
  multiplexGeneralInputsProps,
  previousJobComparisonStatus,
  disableIfNoJob = false,
  missingInputsBySlideId,
  showValidationMessages = true,
}) => {
  const {
    selectedThresholdJobId,
    setSelectedThresholdJobId,
    thresholdJobSelectionMode = 'single',
    setThresholdJobSelectionMode,
    selectedThresholdJobIds = [],
    setSelectedThresholdJobIds,
    thresholdMergeState,
    setThresholdConflictResolutions,
    restoredThresholdJobOrchestrationId,
    onThresholdJobSelect,
    onThresholdJobsSelect,
    onCellRulesChange,
    restoredCellRules,
    selectedCellRulesPanelId,
    setSelectedCellRulesPanelId,
    selectedCellTypingJobId,
    setSelectedCellTypingJobId,
    restoredCellTypingJobOrchestrationId,
    onCellTypingJobSelect,
    selectedNeighborhoodClustersJobId,
    setSelectedNeighborhoodClustersJobId,
    restoredNeighborhoodClustersJobOrchestrationId,
    onNeighborhoodClustersJobSelect,
    binaryClassifierSelectionMode = 'single',
    setBinaryClassifierSelectionMode,
    cellTypingJobError,
    pipRunName,
    autoAssignLastJob = true,
    autoAssignLastPanel = true,
  } = multiplexGeneralInputsProps ?? {};

  const {
    isLoadingThresholdJobs,
    hasThresholdJobs,
    isLoadingPanels,
    hasPanels,
    isLoadingCellTypingJobs,
    hasCellTypingJobs,
    isLoadingNeighborhoodJobs,
    hasNeighborhoodJobs,
    isLoadingGeneralInputs,
  } = useGeneralInputAvailability({
    studyId,
    pipRunName,
    enabled: Boolean(multiplexGeneralInputsProps),
  });

  const {
    slidesById,
    modelsByStain,
    postprocessedArtifactsByStain,
    cellQuantificationArtifactsByStain,
    areaQuantificationArtifactsByStain,
    pipsCellsArtifactsByStain,
    pipsAreaInstancesArtifactsByStain,
    slideSegmentationsTableArtifactsByStain,
    slideDetectionsTableArtifactsByStain,
  } = useMemo(() => {
    const slidesByIdInner = keyBy(slides, 'slideId');

    return {
      slidesById: slidesByIdInner,
      modelsByStain: getModelsByStain(slidesByIdInner, inferenceModels),
      postprocessedArtifactsByStain: getResultsByStainByResultName(slidesByIdInner, postprocessedArtifacts),
      cellQuantificationArtifactsByStain: getResultsByStainByResultName(slidesByIdInner, cellQuantificationArtifacts),
      areaQuantificationArtifactsByStain: getResultsByStainByResultName(slidesByIdInner, areaQuantificationArtifacts),
      pipsCellsArtifactsByStain: getResultsByStainByResultName(slidesByIdInner, pipsResultsArtifacts),
      pipsAreaInstancesArtifactsByStain: getResultsByStainByResultName(slidesByIdInner, pipsAreaInstancesArtifacts),
      slideSegmentationsTableArtifactsByStain: getResultsByStainByResultName(
        slidesByIdInner,
        slideSegmentationsTableArtifacts
      ),
      slideDetectionsTableArtifactsByStain: getResultsByStainByResultName(
        slidesByIdInner,
        slideDetectionsTableArtifacts
      ),
    };
  }, [
    slides,
    inferenceModels,
    postprocessedArtifacts,
    pipsResultsArtifacts,
    pipsAreaInstancesArtifacts,
    cellQuantificationArtifacts,
    areaQuantificationArtifacts,
    slideSegmentationsTableArtifacts,
    slideDetectionsTableArtifacts,
  ]);

  const selectedCellSegmentationModelIds = uniq(
    compact(
      map(values(selectedOrchestrations), (selectedByType) => {
        const selectedCellSegmentation = selectedByType?.[modelTypeCellSegmentation.apiModelValue];
        return selectedCellSegmentation?.model?.modelId ?? getModelId(selectedCellSegmentation?.model?.modelUrl);
      })
    )
  );
  const selectedCellSegmentationModelQueries = useQueries({
    queries: map(selectedCellSegmentationModelIds, (modelId) => ({
      queryKey: ['model', modelId],
      queryFn: ({ signal }: { signal?: AbortSignal }) => getModel(modelId, signal),
      retry: false,
      enabled: Boolean(displaySlideDetectionsTable && modelId),
    })),
  });
  // The selected model's declared geometries are its training capability, not proof of what this
  // specific run produced (e.g. no membranal channels were available, so only nuclear was segmented).
  // Whole cell is only offered once the selected orchestration's own job confirms it.
  const selectedCellSegmentationOrchestrationIds = uniq(
    compact(
      map(values(selectedOrchestrations), (selectedByType) => {
        return selectedByType?.[modelTypeCellSegmentation.apiModelValue]?.orchestration?.orchestrationId;
      })
    )
  );
  const { jobsByOrchestrationId: cellSegmentationJobsByOrchestrationId } = useJobsByOrchestrationIds({
    studyId,
    orchestrationIds: selectedCellSegmentationOrchestrationIds,
    enabled: displaySlideDetectionsTable && !isEmpty(selectedCellSegmentationOrchestrationIds),
  });
  const availableCellSegmentationGeometries = getConfirmedCellSegmentationGeometries(
    getSelectedCellSegmentationGeometries(map(selectedCellSegmentationModelQueries, 'data')),
    map(
      selectedCellSegmentationOrchestrationIds,
      (orchestrationId) => cellSegmentationJobsByOrchestrationId[orchestrationId]
    )
  );

  // Collect every orchestration ID that will actually be rendered so jobs can be fetched once, in
  // a small number of batched requests, instead of one request per OrchestrationRow.
  const orchestrationIdsToPrefetchJobsFor = useMemo(() => {
    const orchestrationIds: string[] = [];
    if (displayInferenceModels) {
      orchestrationIds.push(...collectOrchestrationIdsFromModelsByStain(modelsByStain));
    }
    if (displayPostProcessing) {
      orchestrationIds.push(...collectOrchestrationIdsFromOrchestrationsByStain(postprocessedArtifactsByStain));
    }
    if (displayQuantification) {
      orchestrationIds.push(...collectOrchestrationIdsFromOrchestrationsByStain(cellQuantificationArtifactsByStain));
      orchestrationIds.push(...collectOrchestrationIdsFromOrchestrationsByStain(areaQuantificationArtifactsByStain));
    }
    if (displayPipsResults) {
      orchestrationIds.push(...collectOrchestrationIdsFromOrchestrationsByStain(pipsCellsArtifactsByStain));
    }
    if (displayPipsAreaInstancesResults) {
      orchestrationIds.push(...collectOrchestrationIdsFromOrchestrationsByStain(pipsAreaInstancesArtifactsByStain));
    }
    if (displaySlideSegmentationsTable) {
      orchestrationIds.push(
        ...collectOrchestrationIdsFromOrchestrationsByStain(slideSegmentationsTableArtifactsByStain)
      );
    }
    if (displaySlideDetectionsTable) {
      orchestrationIds.push(...collectOrchestrationIdsFromOrchestrationsByStain(slideDetectionsTableArtifactsByStain));
    }
    return uniq(orchestrationIds);
  }, [
    displayInferenceModels,
    displayPostProcessing,
    displayQuantification,
    displayPipsResults,
    displayPipsAreaInstancesResults,
    displaySlideSegmentationsTable,
    displaySlideDetectionsTable,
    modelsByStain,
    postprocessedArtifactsByStain,
    cellQuantificationArtifactsByStain,
    areaQuantificationArtifactsByStain,
    pipsCellsArtifactsByStain,
    pipsAreaInstancesArtifactsByStain,
    slideSegmentationsTableArtifactsByStain,
    slideDetectionsTableArtifactsByStain,
  ]);

  const hasAttemptedLatestFallbackSelectionRef = useRef(false);

  useEffect(() => {
    if (!autoSelectLatestFallbackInputs || hasAttemptedLatestFallbackSelectionRef.current) {
      return;
    }

    if (
      isLoading ||
      isEmpty(slides) ||
      isLoadingInferenceResults ||
      (displaySlideDetectionsTable && isLoadingSlideDetectionsTable)
    ) {
      return;
    }

    if (
      previousJobComparisonStatus?.hasPreviousJobSettings ||
      hasSelectedPostProcessingInputs({
        selectedPostprocessedOrchestrations,
        selectedPipsOrchestrations,
      })
    ) {
      hasAttemptedLatestFallbackSelectionRef.current = true;
      return;
    }

    const cellSegmentationUpdates = buildLatestCellSegmentationSelectionUpdates({
      slides,
      inferenceModels,
      selectedOrchestrations,
    });
    const spotDetectionUpdates = displaySlideDetectionsTable
      ? buildLatestSpotDetectionSelectionUpdates({
          slides,
          slideDetectionsTableArtifacts,
          selectedSlideDetectionsTableOrchestrations,
        })
      : [];

    hasAttemptedLatestFallbackSelectionRef.current = true;

    forEach(cellSegmentationUpdates, ({ slideIds, model, modelType, orchestration }) => {
      setSelectedOrchestrationsByTypeByStain?.(slideIds, model, modelType, orchestration);
    });
    forEach(spotDetectionUpdates, ({ slideIds, resultKey, orchestration }) => {
      updateSelectedSlideDetectionsTableOrchestrations?.(slideIds, resultKey, orchestration);
    });
  }, [
    autoSelectLatestFallbackInputs,
    displaySlideDetectionsTable,
    inferenceModels,
    isLoading,
    isLoadingInferenceResults,
    isLoadingSlideDetectionsTable,
    previousJobComparisonStatus?.hasPreviousJobSettings,
    selectedOrchestrations,
    selectedPipsOrchestrations,
    selectedPostprocessedOrchestrations,
    selectedSlideDetectionsTableOrchestrations,
    setSelectedOrchestrationsByTypeByStain,
    slideDetectionsTableArtifacts,
    slides,
    updateSelectedSlideDetectionsTableOrchestrations,
  ]);

  if (isLoading) {
    return <Skeleton variant="rectangular" height={150} />;
  }

  const shouldShowInferenceResults = displayInferenceModels && !isEmpty(inferenceModels);
  const shouldShowAssignmentAnnotations =
    displayAssignmentAnnotations && !isEmpty(flatMap(values(assignmentAnnotationsByStainType)));
  const shouldShowPostProcessing = displayPostProcessing && !isEmpty(postprocessedArtifacts);
  const shouldShowCellQuantification = displayQuantification && !isEmpty(cellQuantificationArtifacts);
  const shouldShowAreaQuantification = displayQuantification && !isEmpty(areaQuantificationArtifacts);
  const shouldShowPipsResults = displayPipsResults && !isEmpty(pipsResultsArtifacts);
  const shouldShowPipsAreaInstances = displayPipsAreaInstancesResults && !isEmpty(pipsAreaInstancesArtifacts);
  const shouldShowSlideSegmentationsTable =
    displaySlideSegmentationsTable && !isEmpty(slideSegmentationsTableArtifacts);
  const shouldShowSlideDetectionsTable = displaySlideDetectionsTable && !isEmpty(slideDetectionsTableArtifacts);
  const shouldQueryCellTypingGeneralInputs =
    pipRunName === PipRunNameEnum.CellTyping || pipRunName === PipRunNameEnum.GenericPipsRun;
  const shouldQueryNeighborhoodGeneralInputs =
    pipRunName === PipRunNameEnum.NeighborhoodClusters || pipRunName === PipRunNameEnum.GenericPipsRun;
  const shouldShowThresholdJobInput =
    shouldQueryCellTypingGeneralInputs &&
    shouldRevealOptionalSelector({
      isLoading: isLoadingThresholdJobs,
      hasOptions: hasThresholdJobs,
      hasSelection:
        Boolean(selectedThresholdJobId) ||
        !isEmpty(selectedThresholdJobIds) ||
        Boolean(restoredThresholdJobOrchestrationId),
    });
  const shouldShowPanelInput =
    shouldQueryCellTypingGeneralInputs &&
    shouldRevealOptionalSelector({
      isLoading: isLoadingPanels,
      hasOptions: hasPanels,
      hasSelection: Boolean(selectedCellRulesPanelId) || Boolean(restoredCellRules),
    });
  const shouldShowCellTypingJobInput =
    shouldQueryCellTypingGeneralInputs &&
    shouldRevealOptionalSelector({
      isLoading: isLoadingCellTypingJobs,
      hasOptions: hasCellTypingJobs,
      hasSelection: Boolean(selectedCellTypingJobId) || Boolean(restoredCellTypingJobOrchestrationId),
    });
  const shouldShowNeighborhoodJobInput =
    shouldQueryNeighborhoodGeneralInputs &&
    shouldRevealOptionalSelector({
      isLoading: isLoadingNeighborhoodJobs,
      hasOptions: hasNeighborhoodJobs,
      hasSelection:
        Boolean(selectedNeighborhoodClustersJobId) || Boolean(restoredNeighborhoodClustersJobOrchestrationId),
    });
  const shouldShowGeneralInputs =
    Boolean(multiplexGeneralInputsProps) &&
    (shouldShowThresholdJobInput ||
      shouldShowPanelInput ||
      shouldShowCellTypingJobInput ||
      shouldShowNeighborhoodJobInput);
  const pendingResultCategories = compact([
    displayInferenceModels && isLoadingInferenceResults && isEmpty(inferenceModels)
      ? { key: 'inference_results', label: inputCategoryLabels.inference_results }
      : null,
    displayAssignmentAnnotations &&
    isLoadingAssignmentAnnotations &&
    isEmpty(flatMap(values(assignmentAnnotationsByStainType)))
      ? { key: 'assignment_inputs', label: inputCategoryLabels.assignment_inputs }
      : null,
    displayPostProcessing && isLoadingPostProcessing && isEmpty(postprocessedArtifacts)
      ? { key: 'post_processed_results', label: inputCategoryLabels.post_processed_results }
      : null,
    displayQuantification && isLoadingQuantification && isEmpty(cellQuantificationArtifacts)
      ? { key: 'cell_quantification_results', label: inputCategoryLabels.cell_quantification_results }
      : null,
    displayQuantification && isLoadingQuantification && isEmpty(areaQuantificationArtifacts)
      ? { key: 'area_quantification_results', label: inputCategoryLabels.area_quantification_results }
      : null,
    displayPipsResults && isLoadingPipsResults && isEmpty(pipsResultsArtifacts)
      ? { key: 'pips_cells_results', label: inputCategoryLabels.pips_cells_results }
      : null,
    displayPipsAreaInstancesResults && isLoadingPipsAreaInstances && isEmpty(pipsAreaInstancesArtifacts)
      ? { key: 'pips_area_instances', label: inputCategoryLabels.pips_area_instances }
      : null,
    displaySlideSegmentationsTable && isLoadingSlideSegmentationsTable && isEmpty(slideSegmentationsTableArtifacts)
      ? { key: 'slide_segmentations_table_results', label: inputCategoryLabels.slide_segmentations_table_results }
      : null,
    displaySlideDetectionsTable && isLoadingSlideDetectionsTable && isEmpty(slideDetectionsTableArtifacts)
      ? { key: 'slide_detections_table_results', label: inputCategoryLabels.slide_detections_table_results }
      : null,
    isLoadingGeneralInputs ? { key: 'general_inputs', label: 'General Inputs' } : null,
  ]);
  const isLoadingAnyResults =
    (displayInferenceModels && isLoadingInferenceResults) ||
    (displayAssignmentAnnotations && isLoadingAssignmentAnnotations) ||
    (displayPostProcessing && isLoadingPostProcessing) ||
    (displayQuantification && isLoadingQuantification) ||
    (displayPipsResults && isLoadingPipsResults) ||
    (displayPipsAreaInstancesResults && isLoadingPipsAreaInstances) ||
    (displaySlideSegmentationsTable && isLoadingSlideSegmentationsTable) ||
    (displaySlideDetectionsTable && isLoadingSlideDetectionsTable) ||
    isLoadingGeneralInputs;

  // Inference models and post-processing (e.g. PIPS cell table) results are alternative sources
  // for the same underlying cell detection/segmentation input - a study with no inference models
  // yet, but with valid PIPS results to select from, should still render, not be blocked out.
  const isWaitingForInferenceOrPipsAlternative =
    (displayInferenceModels && isLoadingInferenceResults) || (displayPipsResults && isLoadingPipsResults);
  const hasNoInferenceModels = displayInferenceModels && isEmpty(inferenceModels);
  const hasNoPipsResults = !displayPipsResults || isEmpty(pipsResultsArtifacts);
  const hasNoInferenceOrPipsAlternative =
    !isWaitingForInferenceOrPipsAlternative && hasNoInferenceModels && hasNoPipsResults;

  if (!hidePostProcessingInputs && !displayAssignmentAnnotations && hasNoInferenceOrPipsAlternative) {
    return <Typography>No inference models or post-processing results found for this study</Typography>;
  } else if (
    !hidePostProcessingInputs &&
    displayAssignmentAnnotations &&
    hasNoInferenceOrPipsAlternative &&
    !isLoadingAssignmentAnnotations &&
    isEmpty(flatMap(values(assignmentAnnotationsByStainType)))
  ) {
    return (
      <Typography>
        No inference models, post-processing results, or annotation assignments found for this study
      </Typography>
    );
  } else {
    return (
      <JobsByOrchestrationIdProvider studyId={studyId} orchestrationIds={orchestrationIdsToPrefetchJobsFor}>
        <Grid container spacing={2} mb={1}>
          <Grid item container xs={6} spacing={5} py={1} direction="column" minWidth={0}>
            {setHidePostProcessingInputs && (
              <Grid item>
                <FormControlLabel
                  control={
                    <Switch
                      checked={hidePostProcessingInputs}
                      onChange={(event) => setHidePostProcessingInputs(event.target.checked)}
                    />
                  }
                  label="Hide post processing as inputs"
                />
              </Grid>
            )}
            {isLoadingAnyResults && (
              <Grid item>
                <Typography variant="body2" color="text.secondary">
                  Loading available results...
                </Typography>
              </Grid>
            )}
            {map(pendingResultCategories, ({ key, label }) => (
              <Grid item key={key}>
                <Typography variant="body2" color="text.secondary">
                  Loading {label}...
                </Typography>
              </Grid>
            ))}
            {shouldShowInferenceResults && (
              <InferenceModelsSection
                studyId={studyId}
                modelsByStain={modelsByStain}
                slides={slides}
                selectedOrchestrations={selectedOrchestrations}
                setSelectedOrchestrationsByTypeByStain={setSelectedOrchestrationsByTypeByStain}
                setSelectedOrchestrationsByResultName={updateSelectedPostprocessedOrchestrations}
                modelPreferencesByModelId={modelPreferencesByModelId}
                setModelPreferencesByModelId={setModelPreferencesByModelId}
                setSelectedOrchestrationsByClassificationType={setSelectedOrchestrationsByClassificationType}
                setIntensityClassificationBinning={setIntensityClassificationBinning}
                binaryClassifierSelectionMode={binaryClassifierSelectionMode}
                setBinaryClassifierSelectionMode={setBinaryClassifierSelectionMode}
                disableIfNoJob={disableIfNoJob}
              />
            )}
            {shouldShowAssignmentAnnotations && (
              <AssignmentAnnotationsSection
                assignmentAnnotationsByStainType={assignmentAnnotationsByStainType || {}}
                selectedAssignments={selectedAssignments || {}}
                addNewOverrideAssignment={addNewOverrideAssignment}
                updateSelectedAssignment={updateSelectedAssignment}
                removeSelectedAssignment={removeSelectedAssignment}
              />
            )}
            {shouldShowPostProcessing && (
              <GenericResultsSection
                modelPreferencesByModelId={modelPreferencesByModelId}
                studyId={studyId}
                title={inputCategoryLabels.post_processed_results}
                artifactsByStain={postprocessedArtifactsByStain}
                slides={slides}
                selectedOrchestrations={selectedPostprocessedOrchestrations}
                setSelectedOrchestrations={updateSelectedPostprocessedOrchestrations}
                setModelPreferencesByModelId={setModelPreferencesByModelId}
                setSelectedOrchestrationsByClassificationType={setSelectedOrchestrationsByClassificationType}
                setIntensityClassificationBinning={setIntensityClassificationBinning}
                disableIfNoJob={disableIfNoJob}
              />
            )}
            {shouldShowCellQuantification && (
              <GenericResultsSection
                modelPreferencesByModelId={modelPreferencesByModelId}
                studyId={studyId}
                title={inputCategoryLabels.cell_quantification_results}
                artifactsByStain={cellQuantificationArtifactsByStain}
                slides={slides}
                selectedOrchestrations={selectedCellQuantificationOrchestrations}
                setSelectedOrchestrations={updateSelectedCellQuantificationOrchestrations}
                setModelPreferencesByModelId={setModelPreferencesByModelId}
                setSelectedOrchestrationsByClassificationType={setSelectedOrchestrationsByClassificationType}
                setIntensityClassificationBinning={setIntensityClassificationBinning}
                disableIfNoJob={disableIfNoJob}
              />
            )}
            {shouldShowAreaQuantification && (
              <GenericResultsSection
                modelPreferencesByModelId={modelPreferencesByModelId}
                studyId={studyId}
                title={inputCategoryLabels.area_quantification_results}
                artifactsByStain={areaQuantificationArtifactsByStain}
                slides={slides}
                selectedOrchestrations={selectedAreaQuantificationOrchestrations}
                setSelectedOrchestrations={updateSelectedAreaQuantificationOrchestrations}
                setModelPreferencesByModelId={setModelPreferencesByModelId}
                setSelectedOrchestrationsByClassificationType={setSelectedOrchestrationsByClassificationType}
                setIntensityClassificationBinning={setIntensityClassificationBinning}
                disableIfNoJob={disableIfNoJob}
              />
            )}
            {shouldShowPipsResults && (
              <GenericResultsSection
                modelPreferencesByModelId={modelPreferencesByModelId}
                studyId={studyId}
                title={inputCategoryLabels.pips_cells_results}
                artifactsByStain={pipsCellsArtifactsByStain}
                slides={slides}
                selectedOrchestrations={selectedPipsOrchestrations}
                setSelectedOrchestrations={updateSelectedPipsOrchestrations}
                setModelPreferencesByModelId={setModelPreferencesByModelId}
                setSelectedOrchestrationsByClassificationType={setSelectedOrchestrationsByClassificationType}
                setIntensityClassificationBinning={setIntensityClassificationBinning}
                disableIfNoJob={disableIfNoJob}
              />
            )}
            {shouldShowPipsAreaInstances && (
              <GenericResultsSection
                modelPreferencesByModelId={modelPreferencesByModelId}
                studyId={studyId}
                title={inputCategoryLabels.pips_area_instances}
                artifactsByStain={pipsAreaInstancesArtifactsByStain}
                slides={slides}
                selectedOrchestrations={selectedPipsAreaInstancesOrchestrations}
                setSelectedOrchestrations={updateSelectedPipsAreaInstancesOrchestrations}
                setModelPreferencesByModelId={setModelPreferencesByModelId}
                setSelectedOrchestrationsByClassificationType={setSelectedOrchestrationsByClassificationType}
                setIntensityClassificationBinning={setIntensityClassificationBinning}
                disableIfNoJob={disableIfNoJob}
              />
            )}
            {shouldShowSlideSegmentationsTable && (
              <GenericResultsSection
                modelPreferencesByModelId={modelPreferencesByModelId}
                studyId={studyId}
                title={inputCategoryLabels.slide_segmentations_table_results}
                artifactsByStain={slideSegmentationsTableArtifactsByStain}
                slides={slides}
                selectedOrchestrations={selectedSlideSegmentationsTableOrchestrations}
                setSelectedOrchestrations={updateSelectedSlideSegmentationsTableOrchestrations}
                setModelPreferencesByModelId={setModelPreferencesByModelId}
                setSelectedOrchestrationsByClassificationType={setSelectedOrchestrationsByClassificationType}
                setIntensityClassificationBinning={setIntensityClassificationBinning}
                disableIfNoJob={disableIfNoJob}
              />
            )}
            {shouldShowSlideDetectionsTable && (
              <GenericResultsSection
                modelPreferencesByModelId={modelPreferencesByModelId}
                studyId={studyId}
                title={inputCategoryLabels.slide_detections_table_results}
                artifactsByStain={slideDetectionsTableArtifactsByStain}
                slides={slides}
                selectedOrchestrations={selectedSlideDetectionsTableOrchestrations}
                setSelectedOrchestrations={updateSelectedSlideDetectionsTableOrchestrations}
                setModelPreferencesByModelId={setModelPreferencesByModelId}
                setSelectedOrchestrationsByClassificationType={setSelectedOrchestrationsByClassificationType}
                setIntensityClassificationBinning={setIntensityClassificationBinning}
                enableSpotDetectionSetup
                availableCellSegmentationGeometries={availableCellSegmentationGeometries}
                disableIfNoJob={disableIfNoJob}
              />
            )}
            {shouldShowGeneralInputs && (
              <Grid item>
                <Typography>General Inputs:</Typography>
                <Grid
                  item
                  container
                  spacing={1}
                  direction="column"
                  mt={1}
                  sx={{
                    '&>.MuiGrid-item': {
                      maxWidth: 800,
                    },
                  }}
                >
                  {shouldShowThresholdJobInput && (
                    <Grid item>
                      <Grid container spacing={1.25} direction="column">
                        <Grid item>
                          <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={thresholdJobSelectionMode}
                            onChange={(_event, nextMode: ThresholdJobSelectionMode | null) => {
                              if (nextMode) {
                                setThresholdJobSelectionMode(nextMode);
                              }
                            }}
                            aria-label="Threshold job selection mode"
                          >
                            <ToggleButton value="single" aria-label="Single threshold job">
                              Single job
                            </ToggleButton>
                            <ToggleButton value="multiple" aria-label="Multiple threshold jobs">
                              Multiple jobs
                            </ToggleButton>
                          </ToggleButtonGroup>
                        </Grid>
                        <Grid item>
                          {thresholdJobSelectionMode === 'multiple' ? (
                            <Grid container spacing={1.25} direction="column">
                              <Grid item>
                                <ThresholdJobMultiSelect
                                  selectedThresholdJobIds={selectedThresholdJobIds}
                                  setSelectedThresholdJobIds={setSelectedThresholdJobIds}
                                  restoredJobOrchestrationId={restoredThresholdJobOrchestrationId}
                                  onLoadFullJobs={onThresholdJobsSelect}
                                  studyId={studyId}
                                />
                              </Grid>
                              <Grid item>
                                <ThresholdMergePreview
                                  mergeState={thresholdMergeState}
                                  setConflictResolutions={setThresholdConflictResolutions}
                                />
                              </Grid>
                            </Grid>
                          ) : (
                            <ThresholdJobSelect
                              selectedThresholdJobId={selectedThresholdJobId}
                              setSelectedThresholdJobId={setSelectedThresholdJobId}
                              restoredJobOrchestrationId={restoredThresholdJobOrchestrationId}
                              onLoadFullJob={onThresholdJobSelect}
                              autoAssignLastJob={autoAssignLastJob}
                              studyId={studyId}
                            />
                          )}
                        </Grid>
                      </Grid>
                    </Grid>
                  )}
                  {shouldShowPanelInput && (
                    <Grid item>
                      <CellRulesFromPanelSelect
                        studyId={studyId}
                        selectedPanelId={selectedCellRulesPanelId}
                        onSelectPanel={setSelectedCellRulesPanelId}
                        onCellRulesChange={onCellRulesChange}
                        autoAssignLastPanel={autoAssignLastPanel}
                        restoredCellRules={restoredCellRules}
                      />
                    </Grid>
                  )}
                  {shouldShowCellTypingJobInput && (
                    <Grid item>
                      <CellTypingJobSelect
                        selectedCellTypingJobId={selectedCellTypingJobId}
                        setSelectedCellTypingJobId={setSelectedCellTypingJobId}
                        restoredJobOrchestrationId={restoredCellTypingJobOrchestrationId}
                        onJobSelect={onCellTypingJobSelect}
                        autoAssignLastJob={autoAssignLastJob}
                        studyId={studyId}
                        cellTypingJobError={cellTypingJobError}
                      />
                    </Grid>
                  )}
                  {shouldShowNeighborhoodJobInput && (
                    <Grid item>
                      <NeighborhoodClustersJobSelect
                        selectedNeighborhoodClustersJobId={selectedNeighborhoodClustersJobId}
                        setSelectedNeighborhoodClustersJobId={setSelectedNeighborhoodClustersJobId}
                        restoredJobOrchestrationId={restoredNeighborhoodClustersJobOrchestrationId}
                        onJobSelect={onNeighborhoodClustersJobSelect}
                        autoAssignLastJob={autoAssignLastJob}
                        studyId={studyId}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            )}
          </Grid>
          <Grid item xs={6}>
            {previousJobComparisonStatus?.hasPreviousJobSettings && (
              <Grid item>
                <PreviousJobComparisonAlert
                  status={previousJobComparisonStatus}
                  modifiedMessage="Selected results / inputs modified from previous job."
                  matchMessage="Selected results / inputs match previous job."
                />
              </Grid>
            )}
            <Summary
              modelsType={modelsType}
              slides={slidesById}
              selectedModelOrchestrations={selectedOrchestrations}
              selectedOrchestrationsByResultName={[
                selectedPostprocessedOrchestrations,
                selectedPipsOrchestrations,
                selectedPipsAreaInstancesOrchestrations,
                selectedCellQuantificationOrchestrations,
                selectedAreaQuantificationOrchestrations,
                selectedSlideSegmentationsTableOrchestrations,
                selectedSlideDetectionsTableOrchestrations,
              ]}
              selectedAssignments={selectedAssignments}
              removeSelectedModels={(slideIds) => {
                setSelectedOrchestrationsByTypeByStain(slideIds);
                setSelectedPostprocessedOrchestrationsByFlowClassNameByStain(slideIds);
              }}
              displayAssignmentAnnotations={displayAssignmentAnnotations}
              selectedSlideRegistrationsByOrchestration={selectedSlideRegistrations || {}}
              missingInputsBySlideId={missingInputsBySlideId}
              showValidationMessages={showValidationMessages}
            />
          </Grid>
        </Grid>
      </JobsByOrchestrationIdProvider>
    );
  }
};

export default InferenceModelsForSlides;
